import {skipToken} from '@reduxjs/toolkit/query';
import {closeDialog} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  TestDriveResponseBody,
  useCreateTestDriveMutation,
  useCreateTestDriveWithBusinessCaseMutation,
  useUpdateTestDriveMutation,
} from '@dms/api/sales';
import {i18n} from '@dms/i18n';
import {
  castTimeToDateISOString,
  getCustomerName,
  handleApiError,
  LevelIndicator,
  useUpdateOdometer,
} from '@dms/shared';

import {ApiError, isNotNilOrEmpty} from 'shared/web';

import {TEST_DRIVE_DIALOG_ID} from '../../../constants/TEST_DRIVE_DIALOG_ID';
import {TestDriveDialogSchemaType} from '../schemas/TestDriveDialogSchema';
import {getMileageUpdateCommand} from '../utils/getMileageUpdateCommand';
import {getTestDriveLevelValues} from '../utils/getTestDriveLevelValues';

interface UseTestDriveMileageSubmitProps {
  /** Nil in create mode — the backend finds or creates the business case on save. */
  businessCaseId?: string;
  customerId?: string;
  selectedVehicleId: string | null;
  selectedBranchId: string | null;
  levelIndicators: LevelIndicator[];
  testDrive?: TestDriveResponseBody;
  vehicleMileage?: number;
}

const buildTestDriveRequestBody = (
  formData: TestDriveDialogSchemaType,
  levelIndicators: LevelIndicator[]
) => ({
  userId: formData.userId,
  state: formData.state,
  note: formData.note,
  scheduledAt: castTimeToDateISOString(formData.scheduledAtDate, formData.scheduledAtTime),
  ...getTestDriveLevelValues(formData, levelIndicators),
  mileageBefore: formData.mileageBefore,
  mileageAfter: formData.mileageAfter,
  internalNote: formData.internalNote,
  customerFeedback: formData.customerFeedback,
  customerName: formData.customerName ?? null,
  customerDriversLicence: formData.customerDriversLicence ?? null,
  vehicleId: formData.vehicleId ?? undefined,
  returnedAt:
    formData.returnedAtDate && formData.returnedAtTime
      ? castTimeToDateISOString(formData.returnedAtDate, formData.returnedAtTime)
      : null,
});

export const useTestDriveMileageSubmit = (props: UseTestDriveMileageSubmitProps) => {
  const [createTestDrive, {isLoading: isCreating}] = useCreateTestDriveMutation();
  const [createTestDriveWithBusinessCase, {isLoading: isCreatingWithBusinessCase}] =
    useCreateTestDriveWithBusinessCaseMutation();
  const [updateTestDrive, {isLoading: isUpdating}] = useUpdateTestDriveMutation();
  const [updateOdometer] = useUpdateOdometer(props.selectedVehicleId);

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(props.customerId) ? {customerId: props.customerId} : skipToken
  );

  const saveEditedTestDrive = async (
    businessCaseId: string,
    values: ReturnType<typeof buildTestDriveRequestBody>
  ): Promise<string | null> => {
    if (!isNotNilOrEmpty(props.testDrive?.id)) {
      return null;
    }

    return await updateTestDrive({
      testDriveId: props.testDrive.id,
      businessCaseId,
      updateTestDriveRequestBody: values,
    })
      .unwrap()
      .then(() => businessCaseId)
      .catch((error: ApiError) => {
        handleApiError(error);
        return null;
      });
  };

  const saveCreatedTestDrive = async (
    businessCaseId: string,
    values: ReturnType<typeof buildTestDriveRequestBody>
  ): Promise<string | null> =>
    await createTestDrive({
      businessCaseId,
      createTestDriveRequestBody: values,
    })
      .unwrap()
      .then(() => businessCaseId)
      .catch((error: ApiError) => {
        handleApiError(error);
        return null;
      });

  const saveCreatedTestDriveWithBusinessCase = async (
    values: ReturnType<typeof buildTestDriveRequestBody>
  ): Promise<string | null> => {
    if (
      isNil(props.customerId) ||
      isNil(props.selectedVehicleId) ||
      isNil(props.selectedBranchId)
    ) {
      return null;
    }

    return await createTestDriveWithBusinessCase({
      createTestDriveWithBusinessCaseRequestBody: {
        ...values,
        customerId: props.customerId,
        vehicleId: props.selectedVehicleId,
        branchId: props.selectedBranchId,
      },
    })
      .unwrap()
      .then((response) => response.businessCaseId)
      .catch((error: ApiError) => {
        handleApiError(error);
        return null;
      });
  };

  const handleMileageUpdate = async (
    recordId: string,
    values: ReturnType<typeof buildTestDriveRequestBody>
  ) => {
    if (!props.selectedVehicleId) {
      return;
    }

    const recordTitle = i18n.t('page.businessCase.title', {name: getCustomerName(customer)});

    const command = getMileageUpdateCommand({
      recordId,
      previousValues: {
        mileageBefore: props.testDrive?.mileageBefore ?? props.vehicleMileage,
        mileageAfter: props.testDrive?.mileageAfter ?? props.vehicleMileage,
      },
      submittedValues: {
        mileageBefore: values.mileageBefore,
        mileageAfter: values.mileageAfter,
      },
    });

    if (command) {
      await updateOdometer({...command, recordTitle});
    }
  };

  const handleSubmit = async (formData: TestDriveDialogSchemaType) => {
    const values = buildTestDriveRequestBody(formData, props.levelIndicators);

    const businessCaseId = await match(props)
      .with(
        {testDrive: {id: Pattern.string.minLength(1)}, businessCaseId: Pattern.string},
        (submitProps) => saveEditedTestDrive(submitProps.businessCaseId, values)
      )
      .with({businessCaseId: Pattern.string}, (submitProps) =>
        saveCreatedTestDrive(submitProps.businessCaseId, values)
      )
      .otherwise(() => saveCreatedTestDriveWithBusinessCase(values));

    if (isNil(businessCaseId)) {
      return;
    }

    closeDialog(TEST_DRIVE_DIALOG_ID);

    await handleMileageUpdate(businessCaseId, values);
  };

  return {handleSubmit, isSubmitting: isCreating || isCreatingWithBusinessCase || isUpdating};
};
